/**
 * Marketing Dashboard Grid
 * =========================
 * Drag-and-drop, resizable grid for marketing report widgets.
 * Uses react-grid-layout for dashboard builder functionality.
 * Auto-saves layout changes on drag/resize completion.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { toast } from 'sonner';
import { Loader2, LayoutGrid, Check, Save } from 'lucide-react';
import MarketingReportWidget from './MarketingReportWidget';

const API = process.env.REACT_APP_BACKEND_URL;

const MarketingDashboardGrid = ({
  reports,
  onEditReport,
  onDuplicateReport,
  onReportDeleted,
  refreshKey,
  dateRangeFilter,
}) => {
  const [layout, setLayout] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  
  // Observe container width
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  
  // Fetch dashboard layout
  const fetchLayout = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/marketing/dashboard/layout?page=marketing_tracker`);
      const layoutData = response.data;
      
      setWidgets(layoutData.widgets || []);
      
      // Convert widgets to react-grid-layout format
      const gridLayout = (layoutData.widgets || []).map(w => ({
        i: w.id,
        x: w.x || 0,
        y: w.y || 0,
        w: w.w || 6,
        h: w.h || 4,
        minW: w.min_w || 2,
        minH: w.min_h || 2,
        maxW: w.max_w || 12,
        maxH: w.max_h || 8,
      }));
      
      setLayout(gridLayout);
    } catch (error) {
      console.error('Error fetching dashboard layout:', error);
      // Don't show error toast - might just be empty layout
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchLayout();
  }, [fetchLayout, refreshKey]);
  
  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  // Save layout to backend with visual feedback
  const saveLayout = useCallback(async (updatedWidgets) => {
    setSaving(true);
    setSaveStatus('saving');
    
    try {
      await axios.post(`${API}/api/marketing/dashboard/layout?page=marketing_tracker`, {
        widgets: updatedWidgets,
      });
      
      setSaveStatus('saved');
      
      // Clear the "saved" indicator after 2 seconds
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving layout:', error);
      setSaveStatus('error');
      toast.error('Failed to save layout changes');
      
      // Clear error status after 3 seconds
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } finally {
      setSaving(false);
    }
  }, []);
  
  // Handle layout change (called during drag/resize - just update local state)
  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
  }, []);
  
  // Auto-save when drag stops
  const handleDragStop = useCallback((newLayout, oldItem, newItem, placeholder, e, element) => {
    // Update widgets with new positions
    const updatedWidgets = widgets.map(widget => {
      const layoutItem = newLayout.find(l => l.i === widget.id);
      if (layoutItem) {
        return {
          ...widget,
          x: layoutItem.x,
          y: layoutItem.y,
          w: layoutItem.w,
          h: layoutItem.h,
        };
      }
      return widget;
    });
    
    setWidgets(updatedWidgets);
    saveLayout(updatedWidgets);
  }, [widgets, saveLayout]);
  
  // Auto-save when resize stops
  const handleResizeStop = useCallback((newLayout, oldItem, newItem, placeholder, e, element) => {
    // Update widgets with new dimensions
    const updatedWidgets = widgets.map(widget => {
      const layoutItem = newLayout.find(l => l.i === widget.id);
      if (layoutItem) {
        return {
          ...widget,
          x: layoutItem.x,
          y: layoutItem.y,
          w: layoutItem.w,
          h: layoutItem.h,
        };
      }
      return widget;
    });
    
    setWidgets(updatedWidgets);
    saveLayout(updatedWidgets);
  }, [widgets, saveLayout]);
  
  // Remove widget from dashboard
  const handleRemoveWidget = async (widgetId) => {
    try {
      await axios.delete(`${API}/api/marketing/dashboard/widget/${widgetId}?page=marketing_tracker`);
      
      // Update local state
      setWidgets(prev => prev.filter(w => w.id !== widgetId));
      setLayout(prev => prev.filter(l => l.i !== widgetId));
      
      toast.success('Widget removed from dashboard');
    } catch (error) {
      console.error('Error removing widget:', error);
      toast.error('Failed to remove widget');
    }
  };
  
  // Get report for a widget
  const getReportForWidget = (widget) => {
    return reports.find(r => r.id === widget.report_id);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (widgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-muted-foreground border-2 border-dashed rounded-xl">
        <LayoutGrid className="h-12 w-12 mb-4 opacity-30" />
        <p className="text-lg font-medium">No reports on dashboard</p>
        <p className="text-sm mt-1">Click &quot;Add Report +&quot; to create your first report</p>
      </div>
    );
  }
  
  return (
    <div ref={containerRef} className="w-full">
      {/* Auto-save status indicator */}
      {saveStatus && (
        <div 
          className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg transition-all duration-300 ${
            saveStatus === 'saving' 
              ? 'bg-blue-500 text-white' 
              : saveStatus === 'saved' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
          }`}
          data-testid="auto-save-indicator"
        >
          {saveStatus === 'saving' && (
            <>
              <Save className="h-4 w-4 animate-pulse" />
              <span className="text-sm font-medium">Saving layout...</span>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <Check className="h-4 w-4" />
              <span className="text-sm font-medium">Layout saved</span>
            </>
          )}
          {saveStatus === 'error' && (
            <>
              <span className="text-sm font-medium">Failed to save</span>
            </>
          )}
        </div>
      )}
      
      <GridLayout
        className="layout"
        layout={layout}
        cols={12}
        rowHeight={100}
        width={containerWidth}
        onLayoutChange={handleLayoutChange}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        draggableHandle=".widget-drag-handle"
        isResizable={true}
        isDraggable={true}
        compactType="vertical"
        preventCollision={false}
        margin={[16, 16]}
      >
        {widgets.map(widget => {
          const report = getReportForWidget(widget);
          if (!report) return null;
          
          return (
            <div key={widget.id} className="h-full">
              <MarketingReportWidget
                widget={widget}
                report={report}
                onEdit={onEditReport}
                onDuplicate={onDuplicateReport}
                onRemove={handleRemoveWidget}
                dateRangeFilter={dateRangeFilter}
              />
            </div>
          );
        })}
      </GridLayout>
      
      {/* Grid Layout Styles */}
      <style>{`
        .react-grid-item {
          transition: all 200ms ease;
          transition-property: left, top;
        }
        .react-grid-item.cssTransforms {
          transition-property: transform;
        }
        .react-grid-item.resizing {
          z-index: 1;
          will-change: width, height;
          opacity: 0.9;
        }
        .react-grid-item.react-draggable-dragging {
          transition: none;
          z-index: 3;
          will-change: transform;
          opacity: 0.9;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        .react-grid-item.dropping {
          visibility: hidden;
        }
        .react-grid-item > .react-resizable-handle {
          position: absolute;
          width: 20px;
          height: 20px;
        }
        .react-grid-item > .react-resizable-handle::after {
          content: "";
          position: absolute;
          right: 3px;
          bottom: 3px;
          width: 8px;
          height: 8px;
          border-right: 2px solid rgba(0, 0, 0, 0.4);
          border-bottom: 2px solid rgba(0, 0, 0, 0.4);
        }
        .dark .react-grid-item > .react-resizable-handle::after {
          border-right: 2px solid rgba(255, 255, 255, 0.4);
          border-bottom: 2px solid rgba(255, 255, 255, 0.4);
        }
        .react-grid-placeholder {
          background: rgb(59, 130, 246);
          opacity: 0.2;
          transition-duration: 100ms;
          z-index: 2;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
};

export default MarketingDashboardGrid;
